'use client'

import { cn } from '@brew/ui/cn'
import { useState } from 'react'
import { BrewLogo } from '@/components/icons/brew-logo'
import {
  getClientBrandFaviconUrl,
  getClientBrandLogoUrl,
} from '@/lib/brand/client-brand-logo-url'
import { normalizeBrandDomain } from '@/lib/integrations/integration-brand-domains'

export type TemplateBrandLogoProps = {
  /** Brand domain (e.g. "animoto.com"). */
  domain: string
  /** Display name — used for the alt text and the monogram fallback. */
  name: string
  /**
   * Admin-curated logo (`templateBrands.logoUrl`) — a rehosted
   * cdn.brew.new copy, stage 0 of the cascade when present.
   */
  logoUrl?: string
  className?: string
}

/**
 * Brand logo for the templates gallery: curated rehost (when the admin
 * catalog has one) → context.dev Logo Link → Google favicon →
 * first-letter monogram, degrading silently so a missing logo never
 * leaves a broken image (same cascade as `McpClientLogo`).
 *
 * The FALLBACK stages are HOTLINK ONLY — those URLs must go through a
 * raw `<img>`, never `next/image`: the Logo Link license forbids
 * re-hosting, and the endpoint monograms unknown domains, which would
 * poison the shared image-optimizer cache. The curated stage is our own
 * blob copy and rides the same element to keep the onError cascade
 * trivial. See `lib/brand/client-brand-logo-url.ts`.
 */
function resolveLogoSources(domain: string, logoUrl?: string): Array<string> {
  return [
    ...(logoUrl ? [logoUrl] : []),
    getClientBrandLogoUrl(domain),
    getClientBrandFaviconUrl(domain),
  ].filter(Boolean)
}

/**
 * Brew is the one brand in the gallery we hold the artwork for, so it never
 * goes through the lookup cascade: every stage there answers with the full
 * "Brew" lockup, and a wordmark squeezed into a 28px avatar is a white card
 * with unreadable text on it. We draw the app icon instead — the mark alone,
 * at the icon's proportions, in the shape the caller asked for.
 *
 * Any Brew host resolves here: a flow's brand is keyed off the sending
 * domain, which for our own mail is a subdomain.
 */
function isBrewBrandDomain(domain: string): boolean {
  const host = normalizeBrandDomain(domain).toLowerCase()
  return ['brew.new', 'getbrew.ai'].some(
    (root) => host === root || host.endsWith(`.${root}`)
  )
}

/** The mark is 70% of the tile's height on Brew orange — `public/logo/favicon-light.svg`. */
function BrewBrandMark({
  name,
  className,
}: {
  name: string
  className?: string
}) {
  return (
    <span
      aria-label={`${name} logo`}
      className={cn(
        'flex items-center justify-center rounded-full bg-[#FF772D]',
        className
      )}
      role="img"
    >
      <BrewLogo aria-hidden className="h-[70%] w-[46%] text-white" />
    </span>
  )
}

/**
 * The exported wrapper keys the stateful inner component by domain AND
 * curated URL so the fallback cascade RESETS when the same mounted
 * instance is handed a new domain (combobox pill switching brand A → B)
 * or a swapped-in curated logo — otherwise an error stage accrued
 * against the old source would demote the new one's perfectly good URL.
 * '|' cannot appear in a hostname, so the key never collides.
 */
export function TemplateBrandLogo(props: TemplateBrandLogoProps) {
  if (isBrewBrandDomain(props.domain)) {
    return <BrewBrandMark className={props.className} name={props.name} />
  }
  return (
    <TemplateBrandLogoInner
      key={`${props.domain}|${props.logoUrl ?? ''}`}
      {...props}
    />
  )
}

function TemplateBrandLogoInner({
  domain,
  name,
  logoUrl,
  className,
}: TemplateBrandLogoProps) {
  const [stage, setStage] = useState(0)
  const sources = resolveLogoSources(domain, logoUrl)
  const src = sources[stage] ?? ''

  if (!src) {
    return (
      <span
        aria-hidden
        className={cn(
          'flex items-center justify-center rounded-full bg-muted font-semibold text-caption text-muted-foreground',
          className
        )}
      >
        {name.charAt(0).toUpperCase()}
      </span>
    )
  }

  return (
    // biome-ignore lint/performance/noImgElement: fallback stages are hotlink-only (Logo Link license), not Next-optimizable
    // biome-ignore lint/correctness/useImageSize: pre-Ultracite baseline — add dimensions when touching
    <img
      src={src}
      alt={`${name} logo`}
      className={cn('rounded-full bg-white object-contain', className)}
      decoding="async"
      loading="lazy"
      onError={() => setStage((s) => s + 1)}
    />
  )
}
